import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { ClubDetailDialog } from "./ClubDetailDialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  Trophy,
  CheckCircle2,
  Edit3,
  MapPin,
  CalendarDays,
  Loader2,
  Clock,
  AlertCircle,
  Building2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import { cn } from "@/lib/utils";

interface PlayerInfo {
  id: string;
  name: string;
  image: string | null;
}

interface ScheduledMatchDetail {
  id: string;
  leagueId: string | null;
  scheduledDate: string;
  scheduledTime: string;
  location: string;
  court: string | null;
  status:
    | "scheduled"
    | "score_pending"
    | "score_submitted"
    | "completed"
    | "cancelled";
  winner: 1 | 2 | null;
  score: { sets: Array<{ team1: number; team2: number }> } | null;
  scoreSubmittedById: string | null;
  scoreSubmittedAt: string | null;
  scoreSubmittedByName: string | null;
  scoreSubmitterTeam: 1 | 2 | null;
  approvedById: string | null;
  approvedAt: string | null;
  approvedByName: string | null;
  team1Player1: PlayerInfo;
  team1Player2: PlayerInfo | null;
  team2Player1: PlayerInfo;
  team2Player2: PlayerInfo | null;
  homeClubId?: string | null;
  homeClubName?: string | null;
}

function formatTimestamp(value: string | null | undefined): string | null {
  if (!value) return null;
  try {
    const d = parseISO(value);
    if (Number.isNaN(d.getTime())) return null;
    return format(d, "d MMM yyyy · HH:mm", { locale: es });
  } catch {
    return null;
  }
}

function formatScheduledWhen(dateStr: string, timeStr: string): string {
  try {
    const date = parseISO(`${dateStr}T${timeStr || "00:00"}`);
    return format(date, "EEEE d 'de' MMMM yyyy · HH:mm", { locale: es });
  } catch {
    return `${dateStr} ${timeStr}`;
  }
}

function getInitials(name: string): string {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

function teamLabel(match: ScheduledMatchDetail, team: 1 | 2): string {
  if (team === 1) {
    const a = match.team1Player1?.name?.split(" ")[0];
    const b = match.team1Player2?.name?.split(" ")[0];
    return b ? `${a} y ${b}` : a ?? "Equipo 1";
  }
  const a = match.team2Player1?.name?.split(" ")[0];
  const b = match.team2Player2?.name?.split(" ")[0];
  return b ? `${a} y ${b}` : a ?? "Equipo 2";
}

interface Props {
  matchId: string | null;
  onClose: () => void;
  onRequestDelete?: (matchId: string) => void;
  canDelete?: boolean;
}

/**
 * Rich detail dialog for a completed (or score-submitted) scheduled match.
 * Mirrors the mobile detail screen so a player tapping a standalone match
 * on the web sees the same level of information: final score, set-by-set
 * breakdown, winner, who reported, who confirmed and when.
 *
 * Uses `GET /api/scheduled-matches/:id`, which already returns
 * `scoreSubmittedByName`, `approvedByName`, `scoreSubmittedAt` and
 * `approvedAt` — no extra round trips needed.
 */
export function CompletedScheduledMatchDetailsDialog({
  matchId,
  onClose,
  onRequestDelete,
  canDelete = false,
}: Props) {
  const {
    data: match,
    isLoading,
    isError,
    refetch,
    isFetching,
  } = useQuery<ScheduledMatchDetail>({
    queryKey: ["/api/scheduled-matches", matchId],
    enabled: !!matchId,
  });

  return (
    <Dialog
      open={!!matchId}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent
        className="sm:max-w-md max-h-[90vh] flex flex-col"
        data-testid="dialog-scheduled-match-details"
      >
        <DialogHeader className="shrink-0">
          <DialogTitle>Detalle del partido</DialogTitle>
          <DialogDescription>
            Resumen del partido agendado y su resultado.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div
            className="flex items-center justify-center py-10"
            data-testid="state-scheduled-match-loading"
          >
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : isError || !match ? (
          <div
            className="flex flex-col items-center justify-center gap-3 py-10 text-center"
            data-testid="state-scheduled-match-error"
          >
            <AlertCircle className="h-6 w-6 text-destructive" />
            <p className="text-sm text-muted-foreground">
              No se pudo cargar el partido. Inténtalo de nuevo.
            </p>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                void refetch();
              }}
              disabled={isFetching}
              data-testid="button-scheduled-match-retry"
            >
              {isFetching ? (
                <Loader2 className="h-3 w-3 animate-spin mr-2" />
              ) : null}
              Reintentar
            </Button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto space-y-4 pr-1">
              <MatchDetailContent match={match} />
            </div>
            {onRequestDelete &&
            canDelete &&
            (match.status === "scheduled" ||
              match.status === "score_pending") ? (
              <div className="shrink-0 border-t pt-3 mt-2 flex justify-end">
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => onRequestDelete(match.id)}
                  data-testid="button-dialog-delete-scheduled"
                >
                  Eliminar partido
                </Button>
              </div>
            ) : null}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function MatchDetailContent({ match }: { match: ScheduledMatchDetail }) {
  const [clubDetailOpen, setClubDetailOpen] = useState(false);
  const sets = match.score?.sets ?? [];
  const isCompleted = match.status === "completed";
  const winnerLabel = match.winner ? teamLabel(match, match.winner) : null;
  const reportedAt = formatTimestamp(match.scoreSubmittedAt);
  const approvedAt = formatTimestamp(match.approvedAt);

  let setsT1 = 0;
  let setsT2 = 0;
  for (const s of sets) {
    if (s.team1 > s.team2) setsT1 += 1;
    else if (s.team2 > s.team1) setsT2 += 1;
  }
  // Show the winner's set count first in the headline so a team 2 win
  // reads as "Y ganó 2-1" instead of the misleading "Y ganó 1-2".
  const winnerSets = match.winner === 2 ? setsT2 : setsT1;
  const loserSets = match.winner === 2 ? setsT1 : setsT2;

  return (
    <>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm text-muted-foreground min-w-0">
          <CalendarDays className="h-4 w-4 shrink-0" />
          <span className="truncate" data-testid="text-scheduled-when">
            {formatScheduledWhen(match.scheduledDate, match.scheduledTime)}
          </span>
        </div>
        {isCompleted ? (
          <Badge variant="default" className="text-xs">
            Completado
          </Badge>
        ) : (
          <Badge
            variant="outline"
            className="text-xs text-blue-600 border-blue-300"
          >
            Pendiente
          </Badge>
        )}
      </div>

      <div className="rounded-md border bg-card p-3 space-y-1">
        {match.homeClubName ? (
          match.homeClubId ? (
            <button
              type="button"
              onClick={() => setClubDetailOpen(true)}
              className="flex items-center gap-2 text-sm w-full text-left hover-elevate rounded-md -mx-1 px-1 py-0.5"
              data-testid="row-detail-home-club"
              aria-label={`Ver detalles del club ${match.homeClubName}`}
            >
              <Building2 className="h-4 w-4 text-primary shrink-0" />
              <span className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
                Club
              </span>
              <span
                className="truncate font-medium underline-offset-2 hover:underline"
                data-testid="text-detail-home-club"
              >
                {match.homeClubName}
              </span>
            </button>
          ) : (
            <div
              className="flex items-center gap-2 text-sm"
              data-testid="row-detail-home-club"
            >
              <Building2 className="h-4 w-4 text-primary shrink-0" />
              <span className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
                Club
              </span>
              <span
                className="truncate font-medium"
                data-testid="text-detail-home-club"
              >
                {match.homeClubName}
              </span>
            </div>
          )
        ) : null}
        <div className="flex items-center gap-2 text-sm">
          <MapPin className="h-4 w-4 text-primary shrink-0" />
          <span className="truncate">{match.location}</span>
        </div>
        {match.court ? (
          <div className="flex items-center gap-2 text-xs text-muted-foreground pl-6">
            <span>{match.court}</span>
          </div>
        ) : null}
      </div>

      <div className="rounded-md border bg-card divide-y">
        <TeamRow
          label="Equipo 1"
          players={[match.team1Player1, match.team1Player2].filter(
            Boolean,
          ) as PlayerInfo[]}
          isWinner={match.winner === 1}
        />
        <TeamRow
          label="Equipo 2"
          players={[match.team2Player1, match.team2Player2].filter(
            Boolean,
          ) as PlayerInfo[]}
          isWinner={match.winner === 2}
        />
      </div>

      {sets.length > 0 ? (
        <div
          className="rounded-md border bg-card p-3 space-y-3"
          data-testid="card-result-summary"
        >
          <div className="flex items-center gap-2">
            {isCompleted ? (
              <Trophy className="h-4 w-4 text-yellow-500" />
            ) : (
              <Clock className="h-4 w-4 text-muted-foreground" />
            )}
            <span
              className="text-sm font-medium"
              data-testid="text-result-title"
            >
              {isCompleted
                ? winnerLabel
                  ? `${winnerLabel} ganó ${winnerSets}–${loserSets}`
                  : `Resultado final ${setsT1}–${setsT2}`
                : winnerLabel
                  ? `${winnerLabel} reportó ${winnerSets}–${loserSets}`
                  : `Resultado reportado ${setsT1}–${setsT2}`}
            </span>
          </div>

          <div className="grid grid-cols-3 gap-2">
            {sets.map((s, idx) => {
              const t1Won = s.team1 > s.team2;
              const t2Won = s.team2 > s.team1;
              return (
                <div
                  key={idx}
                  className="rounded-md border px-2 py-1.5 text-center"
                  data-testid={`text-result-set-${idx}`}
                >
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                    Set {idx + 1}
                  </div>
                  <div className="flex items-center justify-center gap-1 font-mono text-sm">
                    <span className={cn(t1Won && "font-bold")}>{s.team1}</span>
                    <span className="text-muted-foreground">-</span>
                    <span className={cn(t2Won && "font-bold")}>{s.team2}</span>
                  </div>
                </div>
              );
            })}
          </div>

          {match.scoreSubmittedByName || reportedAt ? (
            <div
              className="flex items-center gap-2 text-xs text-muted-foreground"
              data-testid="text-result-reporter"
            >
              <Edit3 className="h-3 w-3 shrink-0" />
              <span className="truncate">
                Reportado por {match.scoreSubmittedByName ?? "un jugador"}
                {reportedAt ? ` · ${reportedAt}` : ""}
              </span>
            </div>
          ) : null}

          {isCompleted && (match.approvedByName || approvedAt) ? (
            <div
              className="flex items-center gap-2 text-xs text-muted-foreground"
              data-testid="text-result-approver"
            >
              <CheckCircle2 className="h-3 w-3 shrink-0 text-green-600" />
              <span className="truncate">
                Confirmado por {match.approvedByName ?? "un jugador"}
                {approvedAt ? ` · ${approvedAt}` : ""}
              </span>
            </div>
          ) : null}
        </div>
      ) : null}
      <ClubDetailDialog
        clubId={match.homeClubId ?? null}
        open={clubDetailOpen}
        onOpenChange={setClubDetailOpen}
      />
    </>
  );
}

function TeamRow({
  label,
  players,
  isWinner,
}: {
  label: string;
  players: PlayerInfo[];
  isWinner: boolean;
}) {
  return (
    <div className="flex items-center gap-3 p-3">
      <div className="flex -space-x-2">
        {players.map((p) => (
          <Avatar key={p.id} className="h-8 w-8 border-2 border-background">
            <AvatarImage src={p.image || undefined} />
            <AvatarFallback className="text-[10px]">
              {getInitials(p.name)}
            </AvatarFallback>
          </Avatar>
        ))}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
          {label}
        </div>
        <div className="text-sm font-medium truncate">
          {players.map((p) => p.name).join(" / ") || "—"}
        </div>
      </div>
      {isWinner ? (
        <Trophy
          className="h-4 w-4 text-yellow-500 shrink-0"
          data-testid={`icon-winner-${label.toLowerCase().replace(/\s+/g, "-")}`}
        />
      ) : null}
    </div>
  );
}
